import { Modal, Steps, Upload, message, Divider } from 'antd';
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { RevxButton } from 'components/RevxButton';
import './ImportCustomerOrgModal.less';

import type { UploadFile } from 'antd/es/upload/interface';
import api from 'utils/api';
import axios from 'axios';
import { toast, TOAST_TYPE } from 'components/RevxMessage';
import i18n from 'i18n/config';

interface ImportCustomerOrgModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess?: () => void;
}

const ImportCustomerOrgModal = ({
  visible,
  onCancel,
  onSuccess,
}: ImportCustomerOrgModalProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [resultFileUrl, setResultFileUrl] = useState<string>('');

  const handleDownloadTemplate = async () => {
    try {
      console.log('Starting template download');
      const authToken = localStorage.getItem('Authorization') ?? '';
      // 调用下载模板接口
      const downloadTemplateUrl =
        `${process.env.REACT_APP_API_URL}/orgs/saas/org/import/template/download?timestamp=` +
        new Date().getTime();
      const response: any = await axios.get(downloadTemplateUrl, {
        responseType: 'blob',
        headers: {
          'Access-Control-Allow-Credentials': true,
          'Access-Control-Allow-Origin': '*',
          Accept: '*/*',
          Authorization: authToken,
        },
      });

      console.log('Downloaded template, response:', response);
      if (!(response.data instanceof Blob)) {
        console.log('Invalid response format');
        return;
      }
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      console.log('Blob created:', blob);

      // 创建下载链接
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'customer_org_import_template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success('Template downloaded successfully');
      setCurrentStep(1);
    } catch (error) {
      message.error('Failed to download template');
      console.error('Failed to download template:', error);
    }
  };

  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.warning('Please select a file first');
      return;
    }

    const formData = new FormData();
    formData.append('file', fileList[0] as any);
    console.log('begin Uploading file:');
    try {
      setUploading(true);
      const importUrl = `/orgs/saas/org/import`;
      console.log('Import URL:', importUrl);
      const result = await api.post<string>(importUrl, formData, {
        'Content-Type': 'multipart/form-data',
      });
      console.log('Import result:', result);
      message.success('Import successful');
      setResultFileUrl(result);
      setCurrentStep(2);

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      message.error('Import failed');
      console.error('Failed to import:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadResult = () => {
    console.log('Starting result download');
    console.log('Result file URL:', resultFileUrl);
    if (resultFileUrl) {
      downloadFileFromServer(resultFileUrl, 'customer_org_import_result.xlsx');
    }
  };

  const downloadFileFromServer = async (
    fileUrl: string,
    fileName: string
  ): Promise<void> => {
    try {
      const authToken = localStorage.getItem('Authorization') ?? '';
      const url = `${process.env.REACT_APP_API_URL}/file/downloadFile`;
      console.log('Downloading from URL:', url);

      const res = await axios.post(
        url,
        {
          path: fileUrl,
          fileName: fileName,
        },
        {
          responseType: 'blob',
          headers: {
            'Access-Control-Allow-Credentials': true,
            'Access-Control-Allow-Origin': '*',
            Accept: '*/*',
            Authorization: authToken,
          },
        }
      );

      console.log('Response received:', res);

      if (!(res.data instanceof Blob)) {
        console.log('Invalid response format');
        return;
      }

      const blob = new Blob([res.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Server download failed:', error);
      toast(i18n.t('errors.serviceUnavailable'), TOAST_TYPE.error);
      throw error;
    }
  };

  const handleClose = () => {
    setCurrentStep(0);
    setFileList([]);
    setResultFileUrl('');
    onCancel();
  };

  const steps = [
    {
      title: 'Import Template',
      content: (
        <div style={{ padding: '20px 0', textAlign: 'center' }}>
          <p style={{ marginBottom: 16 }}>
            Please download the Customer Organization import template first
          </p>
          <RevxButton
            type="primary"
            brandGreen
            icon={<DownloadOutlined />}
            onClick={handleDownloadTemplate}
          >
            Download Template
          </RevxButton>
        </div>
      ),
    },
    {
      title: 'Import File',
      content: (
        <div style={{ padding: '24px 0', textAlign: 'center' }}>
          <p style={{ marginBottom: 16 }}>
            Please fill out the template and import the file (supports .xlsx
            format)
          </p>
          <Upload
            fileList={fileList}
            beforeUpload={(file) => {
              // 验证文件类型
              const isExcel =
                file.type ===
                  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                file.type === 'application/vnd.ms-excel';
              if (!isExcel) {
                message.error('Only Excel files are allowed!');
                return false;
              }
              setFileList([file]);
              return false;
            }}
            onRemove={() => {
              setFileList([]);
            }}
            maxCount={1}
          >
            <RevxButton icon={<UploadOutlined />}>Select File</RevxButton>
          </Upload>
        </div>
      ),
    },
    {
      title: 'Import Result',
      content: (
        <div style={{ padding: '24px 0', textAlign: 'center' }}>
          <p style={{ marginBottom: 16, color: '#52c41a' }}>
            ✓ Import completed!
          </p>
          <p style={{ marginBottom: 16 }}>
            Please download the imported result file to view detailed
            information
          </p>
        </div>
      ),
    },
  ];

  return (
    <Modal
      title="Import Customer Organization"
      visible={visible}
      onCancel={handleClose}
      wrapClassName="import-customer-org-modal"
      footer={
        <>
          {currentStep === 0 && (
            <>
              <RevxButton
                type="primary"
                brandGreen
                onClick={() => setCurrentStep(1)}
              >
                Next
              </RevxButton>
              <Divider type="vertical" />
              <RevxButton onClick={handleClose} brandGreen>
                Cancel
              </RevxButton>
            </>
          )}
          {currentStep === 1 && (
            <>
              <RevxButton
                type="primary"
                brandGreen
                onClick={handleUpload}
                loading={uploading}
                disabled={fileList.length === 0}
              >
                Start Import
              </RevxButton>
              <Divider type="vertical" />
              <RevxButton onClick={() => setCurrentStep(0)} brandGreen>
                Back
              </RevxButton>
              <Divider type="vertical" />
              <RevxButton onClick={handleClose} brandGreen>
                Cancel
              </RevxButton>
            </>
          )}
          {currentStep === 2 && (
            <>
              <RevxButton
                type="primary"
                brandGreen
                icon={<DownloadOutlined />}
                onClick={handleDownloadResult}
                disabled={!resultFileUrl}
              >
                Download imported Result
              </RevxButton>
              <Divider type="vertical" />
              <RevxButton onClick={handleClose} brandGreen>
                Done
              </RevxButton>
            </>
          )}
        </>
      }
      width={600}
      destroyOnClose
    >
      <div style={{ position: 'relative' }}>
        <Steps current={currentStep} style={{ marginBottom: 24 }}>
          {steps.map((item) => (
            <Steps.Step key={item.title} title={item.title} />
          ))}
        </Steps>
        {/* 自定义箭头 */}
        <div className="custom-step-arrows">
          <div
            className={`custom-arrow ${currentStep >= 1 ? 'completed' : ''}`}
          >
            →
          </div>
          <div
            className={`custom-arrow ${currentStep >= 2 ? 'completed' : ''}`}
          >
            →
          </div>
        </div>
      </div>
      <div>{steps[currentStep].content}</div>
    </Modal>
  );
};

export default ImportCustomerOrgModal;
